/**
 * apiClient.js
 *
 * Shared axios instance with:
 * - 60-second timeout (GFW often stalls connections for 30-45s before dropping them)
 * - Automatic retry with exponential backoff for network errors
 * - Image compression helper to reduce upload sizes for slow China connections
 */

import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

// ─── Axios instance ────────────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: apiUrl,
  timeout: 60000, // 60 second timeout — GFW often drops after ~30-45s
});

// ─── Retry interceptor ─────────────────────────────────────────────────────────
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000; // start at 2s, doubles each retry

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // Don't retry if the server explicitly returned an error (4xx/5xx)
    // Only retry on network errors (no response) or timeout
    const isNetworkError = !error.response;
    const isTimeout = error.code === "ECONNABORTED";

    if (!isNetworkError && !isTimeout) {
      return Promise.reject(error);
    }

    // Track retry count on the config object
    config._retryCount = config._retryCount || 0;

    if (config._retryCount >= MAX_RETRIES) {
      return Promise.reject(error);
    }

    config._retryCount += 1;

    // Exponential backoff: 2s, 4s, 8s
    const delay = RETRY_DELAY_MS * Math.pow(2, config._retryCount - 1);
    console.log(
      `[apiClient] Network error. Retry ${config._retryCount}/${MAX_RETRIES} in ${delay}ms...`
    );

    await new Promise((resolve) => setTimeout(resolve, delay));
    return apiClient(config);
  }
);

// ─── Auth header helper ────────────────────────────────────────────────────────
export const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

// ─── Image compression helper ──────────────────────────────────────────────────
/**
 * Compresses an image file before uploading.
 * Reduces file size significantly for slow GFW-throttled connections.
 *
 * @param {File} file - The image file to compress
 * @param {number} maxWidthPx - Max width in pixels (default 1200)
 * @param {number} quality - JPEG quality 0-1 (default 0.75)
 * @returns {Promise<File>} - Compressed File object
 */
export const compressImage = (file, maxWidthPx = 1200, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) {
      resolve(file); // not an image, pass through
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");

        // Scale down if wider than maxWidthPx
        let { width, height } = img;
        if (width > maxWidthPx) {
          height = Math.round((height * maxWidthPx) / width);
          width = maxWidthPx;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file); // fallback to original
              return;
            }
            const compressed = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            console.log(
              `[compressImage] ${file.name}: ${(file.size / 1024).toFixed(0)}KB => ${(compressed.size / 1024).toFixed(0)}KB`
            );
            resolve(compressed);
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = () => resolve(file); // fallback
      img.src = event.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

export default apiClient;
